import styled, {
  FloatingPane,
  BOTTOM_CORNERS,
  px,
  paneBoxShadow,
} from "livemap-ui";
import { SignIn } from "./SignIn";
import { Copyright } from "./components/Copyright";
import { Route, Switch, Link, Redirect, useHistory } from "react-router-dom";
import { SignUp } from "./SignUp";
import { Toasts } from "./components/Toasts";
import { useCurrentUser, useSignOut } from "./hooks/current-user";
import { UserPortal } from "./components/UserPortal";
import { SignUpInfo } from "./components/SignUpInfo";

export function App() {
  const { replace, location } = useHistory();
  const user = useCurrentUser();
  const signOut = useSignOut();

  if (user && location.pathname !== "/profile") {
    replace("/profile");
  }

  return (
    <Wrapper>
      <ContentControl>
        <MainContent corners={BOTTOM_CORNERS}>
          <Switch>
            <Route path="/sign-in" exact={true}>
              <SignIn />
            </Route>
            <Route path="/sign-up/:stepNumber?">
              <SignUp />
            </Route>
            <Route path="/profile">
              {user ? <UserPortal user={user} /> : <Redirect to="/sign-in" />}
            </Route>
            <Route path="/" exact={true}>
              <Redirect to="/sign-in" />
            </Route>
          </Switch>
        </MainContent>

        <HelpFooter>
          <Switch>
            <Route path="/sign-in">
              <p>Need an account?</p>
              <Link to="/sign-up">Sign up</Link>
            </Route>
            <Route path="/sign-up" exact={true}>
              <p>Already got an account?</p>
              <Link to="/sign-in">Sign in</Link>
              <SignUpInfo />
            </Route>

            <Route path="/sign-up">
              <SignUpInfo />
            </Route>

            <Route path="/profile">
              <a href="#/sign-out" onClick={signOut}>
                Sign out
              </a>
            </Route>
          </Switch>
        </HelpFooter>
      </ContentControl>

      <Footer>
        <Copyright />
      </Footer>

      <Toasts />
    </Wrapper>
  );
}

const HelpFooter = styled.footer`
  @keyframes help-footer-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  animation: help-footer-fade-in 1500ms;
  animation-delay: 1000ms;
  animation-fill-mode: backwards;

  margin-top: ${px(42)};
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${(p) => p.theme.backgroundColor};

  p {
    opacity: ${(p) => p.theme.disabledOpacity};
  }

  a {
    color: ${(p) => p.theme.detailColor};
    font-size: 18px;
    text-decoration: none;
    font-weight: 500;
    cursor: pointer;
  }
`;

const Footer = styled.footer`
  @keyframes footer-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  animation: help-footer-fade-in 3000ms;
  animation-delay: 2000ms;
  animation-fill-mode: backwards;

  margin-top: auto;
  padding: ${px(24, 24)};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(p) => p.theme.backgroundColor};
  background-size: 100%;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const MainContent = styled(FloatingPane)`
  @keyframes content-fade-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  box-shadow: ${paneBoxShadow(0.4)};
  animation: content-fade-in 1600ms;
  animation-fill-mode: backwards;
  animation-delay: 300ms;

  padding: ${px(24)};
  overflow: visible;
  overflow-x: hidden;
  width: calc(100% + 100px);
  margin-top: 24px;
  margin-left: -50px;

  @media screen and (max-width: 480px) {
    box-shadow: none !important;
    width: 100%;
    margin-left: 0;
  }
`;

const ContentControl = styled.div`
  box-sizing: border-box;
  max-width: ${px(480)};
  margin: 0 auto;
  width: 100%;

  @media screen and (min-width: 480px) {
    width: 400px;
  }
`;
